import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import { obtenerFechaLocal } from "../utils/dateUtils";
import { useFiscal } from "../hooks/useFiscal";

const Entradas = () => {
  const fiscal = useFiscal();
  const [paso, setPaso] = useState(1);
  const [materiales, setMateriales] = useState([]);
  const [materialesOriginal, setMaterialesOriginal] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [materialSeleccionado, setMaterialSeleccionado] = useState(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
 

  const [form, setForm] = useState({
    cantidad: "",
    costo_unitario: "",
    proveedor: "",
    numero_factura: "",
   fecha_adquisicion: obtenerFechaLocal(),
    // ✅ CAMBIO: Campo para el porcentaje de ITBIS
    itbis_porcentaje: "0", 
  });

  // Cargar materiales y categorías
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [resMateriales, resCategorias] = await Promise.all([
          api.get("/materiales"),
          api.get("/categorias"),
        ]);
        setMateriales(resMateriales.data);
        setMaterialesOriginal(resMateriales.data);
        setCategorias(resCategorias.data);
      } catch (error) {
        console.error("Error al cargar materiales/categorías:", error);
      }
    };
    cargarDatos();
  }, []);

  const siguientePaso = () => {
    if (paso === 1 && !materialSeleccionado) {
      alert("⚠️ Por favor selecciona un material");
      return;
    }
    if (paso === 1 && !form.fecha_adquisicion) {
      alert("⚠️ Por favor selecciona una fecha de adquisición");
      return;
    }
    
    // ✅ Validación adicional para Paso 2 (Datos de la Entrada)
    if (paso === 2) {
        if (!form.cantidad || parseFloat(form.cantidad) <= 0) {
          alert("⚠️ La cantidad debe ser mayor a 0");
          return;
        }
        if (!form.costo_unitario || parseFloat(form.costo_unitario) <= 0) {
          alert("⚠️ El costo unitario debe ser mayor a 0");
          return;
        }
    }
    
    setPaso(paso + 1);
  };

  const pasoAnterior = () => setPaso(paso - 1);

  const filtrarPorCategoria = (catId) => {
    setCategoriaFiltro(catId);
    if (catId) {
      const materialesFiltrados = materialesOriginal.filter(
        (m) => m.CategoriaId === parseInt(catId)
      );
      setMateriales(materialesFiltrados);
    } else {
      setMateriales(materialesOriginal);
    }
    setMaterialSeleccionado(null);
  };

  // ✅ FUNCIÓN DE CÁLCULO DE TOTALES
  const calcularTotales = () => {
    const cantidad = parseFloat(form.cantidad || 0);
    const costoUnitario = parseFloat(form.costo_unitario || 0);
    const itbisPorcentaje = parseInt(form.itbis_porcentaje || 0);

    const valorBase = cantidad * costoUnitario;
    const itbisDecimal = itbisPorcentaje / 100;
    const valorItbis = valorBase * itbisDecimal;
    const valorTotalConItbis = valorBase + valorItbis;
    
    return { 
        valorBase: valorBase, 
        valorItbis: valorItbis, 
        valorTotalConItbis: valorTotalConItbis 
    };
  };

  const registrarEntrada = async (e) => {
    e.preventDefault();
    
    // Validaciones (Se movieron al siguientePaso para mejor UX, pero se mantienen aquí por seguridad)
    if (!form.cantidad || parseFloat(form.cantidad) <= 0) {
      alert("⚠️ La cantidad debe ser mayor a 0");
      return;
    }
    if (!form.costo_unitario || parseFloat(form.costo_unitario) <= 0) {
      alert("⚠️ El costo unitario debe ser mayor a 0");
      return;
    }

    try {
      // 💡 Utilizamos la función de cálculo
      const { valorTotalConItbis } = calcularTotales();

      const body = {
        MaterialId: materialSeleccionado.id,
        codigo: materialSeleccionado.codigo,
        descripcion: materialSeleccionado.nombre,
        unidad_medida: materialSeleccionado.unidad || "unidad",
        cantidad: parseFloat(form.cantidad),
        costo_unitario: parseFloat(form.costo_unitario),
        
        // ✅ ITBIS y CÁLCULOS
        itbis: parseInt(form.itbis_porcentaje), // Guardamos el porcentaje
        valor_total: valorTotalConItbis.toFixed(2), // Total (Base + ITBIS)
        
        // ✅ Monto Factura = Valor Total Calculado
        monto_factura: valorTotalConItbis.toFixed(2),
        
        proveedor: form.proveedor,
        numero_factura: form.numero_factura,
        fecha_adquisicion: form.fecha_adquisicion,
      };

      await api.post("/entradas", body);
      
      // 👇 ACTUALIZACIÓN: Alerta mejorada sobre aprobación pendiente
      Swal.fire({
        icon: 'success',
        title: '✅ Entrada Registrada',
        html: `
          <p style="font-size: 15px; margin: 10px 0;">La entrada ha sido registrada correctamente.</p>
          <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin-top: 15px; border-left: 4px solid #f59e0b;">
            <p style="margin: 0; color: #78350f; font-weight: 600;">
              ⏳ <strong>Pendiente de Aprobación</strong>
            </p>
            <p style="margin: 5px 0 0 0; color: #78350f; font-size: 13px;">
              El stock se actualizará una vez que el supervisor apruebe esta entrada.
            </p>
          </div>
        `,
        confirmButtonColor: '#667eea',
        confirmButtonText: 'Entendido'
      });
      
      // Resetear formulario
      setForm({
        cantidad: "",
        costo_unitario: "",
        proveedor: "",
        numero_factura: "",
       fecha_adquisicion: obtenerFechaLocal(),
        itbis_porcentaje: "0",
      });
      setMaterialSeleccionado(null);
      setCategoriaFiltro("");
      setMateriales(materialesOriginal);
      setPaso(1);
    } catch (error) {
      console.error("Error al registrar entrada:", error);
      Swal.fire({
        icon: 'error',
        title: '❌ Error',
        text: 'No se pudo registrar la entrada',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const getCategoriaDelMaterial = () => {
    if (!materialSeleccionado) return null;
    return categorias.find((c) => c.id === materialSeleccionado.CategoriaId);
  };

  

 
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📥 Registrar Entrada de Material</h2>
      
        <div style={styles.progressBar}>
          <div style={{...styles.progressStep, ...(paso >= 1 ? styles.progressStepActive : {})}}>
            <span style={styles.progressNumber}>1</span>
            <span style={styles.progressLabel}>Selección</span>
          </div>
          <div style={styles.progressLine}></div>
          <div style={{...styles.progressStep, ...(paso >= 2 ? styles.progressStepActive : {})}}>
            <span style={styles.progressNumber}>2</span>
            <span style={styles.progressLabel}>Datos</span>
          </div>
          <div style={styles.progressLine}></div>
          <div style={{...styles.progressStep, ...(paso >= 3 ? styles.progressStepActive : {})}}>
            <span style={styles.progressNumber}>3</span>
            <span style={styles.progressLabel}>Confirmar</span>
          </div>
        </div>
      </div>

     

      <div style={styles.content}>
        {/* Paso 1: Selección del Material */}
        {paso === 1 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>
              <span style={styles.stepIcon}>🎯</span>
              Selección del Material
            </h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📂</span>
                Filtrar por Categoría
              </label>
              <select
                style={styles.select}
                value={categoriaFiltro}
                onChange={(e) => filtrarPorCategoria(e.target.value)}
              >
                <option value="">📋 Todas las categorías</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📦</span>
                Material <span style={styles.required}>*</span>
              </label>
              <select
                style={styles.select}
                value={materialSeleccionado?.id || ""}
                onChange={(e) =>
                  setMaterialSeleccionado(
                    materiales.find((m) => m.id === parseInt(e.target.value))
                  )
                }
              >
                <option value="">-- Selecciona un material --</option>
                {materiales.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.codigo} - {m.nombre}
                  </option>
                ))}
              </select>
            </div>

            {materialSeleccionado && (
              <div style={styles.infoCard}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>📌 Código:</span>
                  <span style={styles.infoValue}>{materialSeleccionado.codigo}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>📝 Nombre:</span>
                  <span style={styles.infoValue}>{materialSeleccionado.nombre}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>🏷️ Categoría:</span>
                  <span style={styles.infoBadge}>
                    {getCategoriaDelMaterial()?.nombre || "Sin categoría"}
                  </span>
                </div>
                {materialSeleccionado.ubicacion && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>📍 Ubicación:</span>
                    <span style={styles.infoValue}>{materialSeleccionado.ubicacion}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>📊 Stock Actual:</span>
                  <span style={{...styles.infoValue, color: '#2196F3', fontWeight: 'bold'}}>
                    {materialSeleccionado.stock_actual || 0} {materialSeleccionado.unidad || 'unidades'}
                  </span>
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📅</span>
                Fecha de Adquisición <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                style={styles.input}
                value={form.fecha_adquisicion}
                onChange={(e) => setForm({ ...form, fecha_adquisicion: e.target.value })}
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onClick={siguientePaso}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}

        {/* Paso 2: Datos de la Entrada */}
        {paso === 2 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>
              <span style={styles.stepIcon}>📝</span>
              Datos de la Entrada
            </h3>
            
            {/* 1. FILA SUPERIOR: Número Factura y Proveedor */}
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span style={styles.labelIcon}>🧾</span>
                  Número de Factura
                </label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Ej: 001-0000123"
                  value={form.numero_factura}
                  onChange={(e) => setForm({ ...form, numero_factura: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span style={styles.labelIcon}>🏢</span>
                  Proveedor
                </label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Nombre del proveedor"
                  value={form.proveedor}
                  onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
                />
              </div>
            </div>


            {/* 2. FILA MEDIA: Cantidad, Costo Unitario, ITBIS */}
            <div style={styles.formGrid}> 
              
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span style={styles.labelIcon}>📦</span>
                  Cantidad <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  style={styles.input}
                  placeholder="Ej: 100"
                  value={form.cantidad}
                  onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span style={styles.labelIcon}>💰</span>
                  Costo Unitario (RD$) <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  style={styles.input}
                  placeholder="Ej: 50.00"
                  value={form.costo_unitario}
                  onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })}
                />
              </div>
              
              {/* ✅ CAMPO ITBIS */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {fiscal.impuesto_label || 'ITBIS'} (%) <span style={styles.required}>*</span>
                </label>
                <select
                  style={styles.select}
                  value={form.itbis_porcentaje}
                  onChange={(e) => setForm({ ...form, itbis_porcentaje: e.target.value })}
                >
                  <option value="0">0% (Exento)</option>
                  <option value="16">16% (Tasa Reducida)</option>
                  <option value="18">18% (Tasa Estándar)</option>
                </select>
              </div>
            </div>

            {/* 3. MONTO FACTURA CALCULADO */}
            {form.cantidad && form.costo_unitario && (
                <div style={{...styles.totalCard, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    <span style={styles.totalLabel}>💰 Monto Factura Calculado:</span>
                    <span style={styles.totalValue}>
                        RD$ {calcularTotales().valorTotalConItbis.toFixed(2)}
                    </span>
                </div>
            )}
            
            {/* ❌ ELIMINADO EL CAMPO MONTO FACTURA MANUAL */}
            <div style={styles.formGroup}>
                {/* Espacio para balancear o rellenar con otro campo si es necesario */}
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={pasoAnterior}
              >
                ← Anterior
              </button>
              <button
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onClick={siguientePaso}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}

        {/* Paso 3: Confirmación */}
        {paso === 3 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>
              <span style={styles.stepIcon}>✅</span>
              Confirmar Entrada
            </h3>

            <div style={styles.confirmCard}>
              <div style={styles.confirmSection}>
                <h4 style={styles.confirmSectionTitle}>📦 Material</h4>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Código:</span>
                  <span style={styles.confirmValue}>{materialSeleccionado?.codigo}</span>
                </div>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Nombre:</span>
                  <span style={styles.confirmValue}>{materialSeleccionado?.nombre}</span>
                </div>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Categoría:</span>
                  <span style={styles.confirmBadge}>
                    {getCategoriaDelMaterial()?.nombre || "Sin categoría"}
                  </span>
                </div>
              </div>

              <div style={styles.confirmSection}>
                <h4 style={styles.confirmSectionTitle}>📝 Datos de Entrada</h4>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Cantidad:</span>
                  <span style={styles.confirmValue}>
                    {form.cantidad} {materialSeleccionado?.unidad || 'unidades'}
                  </span>
                </div>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Costo Unitario (Base):</span>
                  <span style={styles.confirmValue}>RD$ {parseFloat(form.costo_unitario || 0).toFixed(2)}</span>
                </div>
                <div style={styles.confirmRow}>
                    <span style={styles.confirmLabel}>{fiscal.impuesto_label || 'ITBIS'} Aplicado:</span>
                    <span style={styles.confirmValue}>{form.itbis_porcentaje}%</span>
                </div>
                <div style={styles.confirmRow}>
                    <span style={styles.confirmLabel}>Valor {fiscal.impuesto_label || 'ITBIS'} (Monto):</span>
                    <span style={styles.confirmValue}>RD$ {calcularTotales().valorItbis.toFixed(2)}</span>
                </div>
                
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Fecha:</span>
                  <span style={styles.confirmValue}>
                    {(() => {
                      const [y, m, d] = form.fecha_adquisicion.split('-');
                      return new Date(y, m - 1, d).toLocaleDateString('es-DO');
                    })()}
                  </span>
                </div>
              </div>

              {(form.proveedor || form.numero_factura) && (
                <div style={styles.confirmSection}>
                  <h4 style={styles.confirmSectionTitle}>🧾 Información Adicional</h4>
                  {form.numero_factura && (
                    <div style={styles.confirmRow}>
                      <span style={styles.confirmLabel}>Nº Factura:</span>
                      <span style={styles.confirmValue}>{form.numero_factura}</span>
                    </div>
                  )}
                  {form.proveedor && (
                    <div style={styles.confirmRow}>
                      <span style={styles.confirmLabel}>Proveedor:</span>
                      <span style={styles.confirmValue}>{form.proveedor}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ✅ TARJETA FINAL: Monto Total Factura (Calculado) */}
            <div style={styles.totalCard}>
              <span style={styles.totalLabel}>💰 Monto Factura Total:</span>
              <span style={styles.totalValue}>
                RD$ {calcularTotales().valorTotalConItbis.toFixed(2)}
              </span>
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={pasoAnterior}
              >
                ← Anterior
              </button>
              <button
                style={{ ...styles.button, ...styles.buttonSuccess }}
                onClick={registrarEntrada}
              >
                ✅ Registrar Entrada
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '20px auto',
    padding: '0 20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    padding: '30px',
    color: 'white',
    marginBottom: '30px',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  title: {
    margin: '0 0 25px 0',
    fontSize: '28px',
    fontWeight: '600',
    textAlign: 'center',
  },
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    opacity: '0.5',
    transition: 'all 0.3s ease',
  },
  progressStepActive: {
    opacity: '1',
  },
  progressNumber: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '18px',
    marginBottom: '8px',
  },
  progressLabel: {
    fontSize: '12px',
    fontWeight: '500',
  },
  progressLine: {
    width: '80px',
    height: '2px',
    background: 'rgba(255, 255, 255, 0.3)',
    margin: '0 10px 20px 10px',
  },
  content: {
    background: 'white',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  stepContent: {
    animation: 'fadeIn 0.3s ease',
  },
  stepTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepIcon: {
    fontSize: '28px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '8px',
  },
  labelIcon: {
    fontSize: '16px',
  },
  required: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    background: 'white',
    cursor: 'pointer',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  infoCard: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '20px',
    marginBottom: '20px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
  },
  infoValue: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
  },
  infoBadge: {
    background: '#667eea',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  totalCard: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
  },
  totalLabel: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'white',
  },
  totalValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'white',
  },
  confirmCard: {
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '25px',
    marginBottom: '25px',
  },
  confirmSection: {
    marginBottom: '25px',
    paddingBottom: '20px',
    borderBottom: '2px solid #e0e0e0',
  },
  confirmSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#667eea',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
  },
  confirmLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500',
  },
  confirmValue: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '600',
  },
  confirmBadge: {
    background: '#667eea',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    marginTop: '30px',
  },
  button: {
    padding: '14px 30px',
    fontSize: '15px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    minWidth: '140px',
  },
  buttonPrimary: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  buttonSecondary: {
    background: '#95a5a6',
    color: 'white',
  },
  buttonSuccess: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    color: 'white',
  },
};

export default Entradas;